import { type Actor, type Edge, type Item } from '@shared/board'
import { type TurnChangeSet } from '@shared/trust'
import { type Rect } from '@shared/whiteboard/geometry'

// A turn's change set as people read it (DESIGN.md 信任層): one row per thing
// on the board — what happened to it, how, and why — in the order the turn
// first touched each. Card text changes join their card's row.

export type Status = 'added' | 'removed' | 'changed'

export type Detail =
  | { kind: 'moved' }
  | { kind: 'resized' }
  | { kind: 'regrouped'; from: string | null; to: string | null }
  | { kind: 'renamed'; from: string; to: string }
  | { kind: 'relabelled'; from: string; to: string }
  | { kind: 'other' }

export type ChangeRow = {
  /** "item:ID" or "edge:ID". */
  key: string
  id: string
  isEdge: boolean
  status: Status
  /** The item as it was before (removed) or is after the turn; null for edges. */
  item: Item | null
  edge: Edge | null
  /** Where it is (or was, when removed), to frame it on the canvas. */
  rect: Rect | null
  details: Detail[]
  text: { before: string; after: string; current: string | null } | null
  /** The reasons the AI gave, deduplicated. */
  intents: string[]
  /** Changed after the turn by these: a revert leaves those parts alone. */
  laterBy: Actor[]
  /** All refs behind this row (the item and its text). */
  refs: string[]
}

type Change = TurnChangeSet['changes'][number]

const rectOf = (i: Item): Rect => ({ x: i.x, y: i.y, w: i.w, h: i.h })

function itemDetails(before: Item, after: Item): Detail[] {
  const out: Detail[] = []
  if (before.x !== after.x || before.y !== after.y) out.push({ kind: 'moved' })
  if (before.w !== after.w || before.h !== after.h) out.push({ kind: 'resized' })
  if ((before.groupId ?? null) !== (after.groupId ?? null))
    out.push({ kind: 'regrouped', from: before.groupId ?? null, to: after.groupId ?? null })
  if (JSON.stringify(before.data) !== JSON.stringify(after.data)) {
    if (before.kind === 'group' && after.kind === 'group' && before.data.title !== after.data.title)
      out.push({ kind: 'renamed', from: before.data.title, to: after.data.title })
    else out.push({ kind: 'other' })
  }
  return out
}

function addActors(into: Actor[], more: Actor[]) {
  for (const a of more) if (!into.some((b) => b.kind === a.kind && b.id === a.id)) into.push(a)
}

export function changeRows(set: TurnChangeSet): ChangeRow[] {
  const rows = new Map<string, ChangeRow>()
  const rowFor = (key: string, id: string, isEdge: boolean): ChangeRow => {
    let r = rows.get(key)
    if (!r) {
      r = {
        key,
        id,
        isEdge,
        status: 'changed',
        item: null,
        edge: null,
        rect: null,
        details: [],
        text: null,
        intents: [],
        laterBy: [],
        refs: [],
      }
      rows.set(key, r)
    }
    return r
  }
  const note = (r: ChangeRow, c: Change) => {
    r.refs.push(c.ref)
    if (c.intent && !r.intents.includes(c.intent)) r.intents.push(c.intent)
    addActors(r.laterBy, c.laterBy)
  }
  for (const c of set.changes) {
    if (c.kind === 'item') {
      const id = c.ref.slice('item:'.length)
      const r = rowFor(`item:${id}`, id, false)
      note(r, c)
      r.status = !c.before ? 'added' : !c.after ? 'removed' : 'changed'
      r.item = c.after ?? c.before
      const at = c.after ?? c.before
      r.rect = at ? rectOf(at) : null
      if (c.before && c.after) r.details = itemDetails(c.before, c.after)
    } else if (c.kind === 'edge') {
      const id = c.ref.slice('edge:'.length)
      const r = rowFor(`edge:${id}`, id, true)
      note(r, c)
      r.status = !c.before ? 'added' : !c.after ? 'removed' : 'changed'
      r.edge = c.after ?? c.before
      if (c.before && c.after && c.before.label !== c.after.label)
        r.details = [{ kind: 'relabelled', from: c.before.label, to: c.after.label }]
    } else {
      const id = c.ref.slice('text:'.length)
      const r = rowFor(`item:${id}`, id, false)
      note(r, c)
      r.text = {
        before: c.before,
        after: c.after,
        current: typeof c.current === 'string' ? c.current : null,
      }
      // A card whose only change is its text has no item state here: the
      // view frames it where it is now.
    }
  }
  return [...rows.values()]
}

/** The ids a row marks on the canvas: the item, or both ends of a connection. */
export function rowIds(r: ChangeRow): string[] {
  if (!r.isEdge) return [r.id]
  return r.edge ? [r.edge.from, r.edge.to] : []
}
